/**
 * Cross-platform defaults applied to a NEWLY created deck / folder / study
 * group. Lives in `shared/` because the values are written into the sync
 * engine and must match on flashka-web and flashka-app.
 */
import { brandColor } from '../ui/styles/brand-colors';

/**
 * The curated neutral shown as the pre-selected "Default" tile in every colour
 * picker. Deliberately NOT one of the vivid `deck-*` palette names: a brand-new
 * deck should read as neutral (and stay readable in dark mode) instead of
 * getting an arbitrary bright colour.
 *
 * Both platforms resolve it through their existing "uncoloured" branch — the
 * `secondary` ramp — so it needs no per-surface hex of its own:
 * - web: `getCustomizedColor` → `bg-secondary-400` (icons/rows),
 *   `useDeckColorHex` → {@link defaultDeckSurfaceHex} for the DS cards' art
 * - app: `useDeckColor` → {@link defaultDeckSurfaceHex},
 *   `getFolderIconColors` → `secondary` entry
 *
 * The card surface is the one value that is NOT mode-invariant: identity
 * colours are the same in both themes by DS design, but a neutral is a
 * SURFACE and has to follow the ramp's dark mirror.
 *
 * Note this is the STORED value; the picker tile itself is drawn in
 * `secondary-50` with a `secondary-600` `Ban` glyph, per the design.
 */
export const DEFAULT_DECK_COLOR = 'secondary';

/**
 * The hex a default (or uncoloured) deck paints its DS card art with —
 * `secondary-100`, which mirrors to `secondary-900` in dark (`#f5f5f5` /
 * `#171717`). One function so web and the app cannot disagree on it.
 */
export const defaultDeckSurfaceHex = (isDark: boolean): string =>
  brandColor('secondary', '100', isDark);

/** True for {@link defaultDeckSurfaceHex} in either theme — "this deck has no colour of its own". */
export const isDefaultDeckSurfaceHex = (hex: string): boolean => {
  const h = hex.toLowerCase();
  return h === DEFAULT_SURFACE_LIGHT || h === DEFAULT_SURFACE_DARK;
};
// Hoisted: `isDefaultDeckSurfaceHex` runs on every deck/folder icon render.
const DEFAULT_SURFACE_LIGHT = defaultDeckSurfaceHex(false).toLowerCase();
const DEFAULT_SURFACE_DARK = defaultDeckSurfaceHex(true).toLowerCase();

/**
 * Emoji offered as the random tag ("emoji/tag" field) of a manually created
 * deck. Curated on purpose — a study-leaning core plus a few playful ones — so
 * a fresh deck lands with something characterful rather than blank, and never
 * with an emoji that reads as an error/status glyph.
 */
/** The study-&-school slice on its own — surfaces that want a strictly
 *  study-flavoured pick (the onboarding first deck's name) draw from this;
 *  {@link DECK_EMOJIS} spreads it so the full set stays one source. */
export const STUDY_DECK_EMOJIS = [
  '📚',
  '📖',
  '📝',
  '✏️',
  '🖊️',
  '📓',
  '📔',
  '📒',
  '📄',
  '📋',
  '📌',
  '🎓',
  '🏫',
  '🧠',
  '💡',
  '🔖',
  '🗂️',
  '📑',
] as const;

export const DECK_EMOJIS = [
  // Study & school
  ...STUDY_DECK_EMOJIS,
  // Science & numbers
  '🔬',
  '🧪',
  '🧬',
  '🔭',
  '⚗️',
  '🧮',
  '📐',
  '📏',
  '📊',
  '📈',
  '⚛️',
  // Fields & professions
  '💻',
  '⚖️',
  '🩺',
  '🏥',
  '🏛️',
  '🌍',
  '🗺️',
  '🎨',
  '🎭',
  '🎼',
  '🏗️',
  '💰',
  '💼',
  '🔧',
  '🌱',
  '🦴',
  '🗿',
  '⛩️',
  // Fun
  '🪎',
  '🚀',
  '⚡',
  '🔥',
  '🌟',
  '🎯',
  '🍀',
  '🦉',
  '🐙',
  '🦄',
  '🪐',
  '🍕',
  '🧃',
  '🎲',
  '🕹️',
  '🧩',
] as const;

/** Random entry from {@link DECK_EMOJIS}. */
export function getRandomDeckEmoji(): string {
  return DECK_EMOJIS[Math.floor(Math.random() * DECK_EMOJIS.length)];
}

/** Random entry from {@link STUDY_DECK_EMOJIS}. */
export function getRandomStudyDeckEmoji(): string {
  return STUDY_DECK_EMOJIS[Math.floor(Math.random() * STUDY_DECK_EMOJIS.length)];
}
